import {
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  Textarea,
} from '@risksmart-app/atomic-ui';
import { type ReactNode, useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

export const FormTextField = ({
  name,
  label,
  placeholder,
  disabled,
  optional,
}: {
  name: string;
  label: ReactNode;
  placeholder?: string;
  disabled?: boolean;
  optional?: boolean;
}) => {
  const { control } = useFormContext();
  const inputId = useId();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={!!fieldState.error || undefined}>
          <FieldLabel htmlFor={inputId}>
            {label}
            {!optional ? (
              <span className={'text-destructive ml-0.5'}>{'*'}</span>
            ) : null}
          </FieldLabel>
          <Input
            id={inputId}
            placeholder={placeholder}
            aria-invalid={!!fieldState.error}
            disabled={disabled}
            {...field}
          />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  );
};

export const FormTextareaField = ({
  name,
  label,
  placeholder,
  optional,
  rows = 3,
}: {
  name: string;
  label: string;
  placeholder?: string;
  optional?: boolean;
  rows?: number;
}) => {
  const { control } = useFormContext();
  const inputId = useId();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={!!fieldState.error || undefined}>
          <FieldLabel htmlFor={inputId}>
            {label}
            {!optional ? (
              <span className={'text-destructive ml-0.5'}>{'*'}</span>
            ) : null}
          </FieldLabel>
          <Textarea
            id={inputId}
            placeholder={placeholder}
            rows={rows}
            aria-invalid={!!fieldState.error}
            {...field}
            value={field.value ?? ''}
          />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  );
};

export const FormSelectField = ({
  name,
  label,
  placeholder,
  options,
  disabled,
  optional,
}: {
  name: string;
  label: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
  optional?: boolean;
}) => {
  const { control } = useFormContext();

  const items = [
    { label: placeholder ?? 'Select...', value: null },
    ...options,
  ];

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={!!fieldState.error || undefined}>
          <FieldLabel>
            {label}
            {!optional ? (
              <span className={'text-destructive ml-0.5'}>{'*'}</span>
            ) : null}
          </FieldLabel>
          <Select
            items={items}
            value={field.value}
            onValueChange={field.onChange}
            disabled={disabled}
          />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  );
};
